/**
 * LoginPage — premium split layout for marine inspection teams.
 *
 *   ┌─────────────────────────────────┬─────────────────────────────┐
 *   │ Brand · headline · cues         │   Sign-in card              │
 *   │ Subtle nautical chart bg        │   Google · email · CTA      │
 *   │ Trust cues (audit-ready, RBAC)  │   Inspector / Viewer cards  │
 *   │                                 │   Demo CTA                  │
 *   └─────────────────────────────────┴─────────────────────────────┘
 *   Footer: T&C · Privacy · Copyright
 *
 * The left panel renders a low-opacity SVG chart layer (depth contours,
 * grid lines, drifting AIS-style dots, a radar arc) in pure SVG/CSS —
 * no images, no third-party deps, fully maintainable. The right card
 * uses Clerk's `<SignIn />` with appearance tokens that match the brand
 * palette pixel-for-pixel.
 *
 * Mobile: collapses to a single column, login card first, brand cues
 * become a single trust strip.
 */
import React from 'react';
import { SignIn } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  FileText,
  Ship,
  Radar,
  ArrowRight,
} from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { isClerkConfigured } from '../lib/clerk-config';

export default function LoginPage() {
  // Clerk's <SignIn /> throws outside <ClerkProvider>, which is skipped when
  // the publishable key is missing/placeholder. Explain instead of crashing.
  if (!isClerkConfigured) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md rounded-xl bg-slate-900/60 border border-slate-800 p-6 text-center">
          <BrandLogo size="lg" tagline tone="dark" />
          <h1 className="text-lg font-bold mt-4">Sign-in not configured</h1>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Authentication is unavailable because <code className="text-cyan-300">REACT_APP_CLERK_PUBLISHABLE_KEY</code>{' '}
            is not set (still a placeholder in <code className="text-cyan-300">frontend/.env.local</code>).
            Fill it from the Clerk dashboard and restart the dev server.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <main id="main-content" className="flex-1 grid lg:grid-cols-[1.05fr_0.95fr] xl:grid-cols-[1.1fr_0.9fr]">
        {/* ── LEFT: brand / product cues ───────────────────────────── */}
        <section className="relative hidden lg:flex flex-col justify-between px-10 xl:px-14 py-10 xl:py-12 overflow-hidden border-r border-slate-800/80">
          <NauticalBackdrop />

          <div className="relative">
            <BrandLogo size="lg" tagline tone="dark" />
          </div>

          <div className="relative max-w-xl">
            <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-[11px] font-bold uppercase tracking-widest text-cyan-300 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Maritime compliance intelligence
            </span>
            <h1 className="text-4xl xl:text-5xl font-bold tracking-tight text-white leading-[1.1]">
              Know every vessel is{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
                inspection-ready
              </span>
              .
            </h1>
            <p className="mt-5 text-lg text-slate-400 leading-relaxed max-w-lg">
              Continuous fleet readiness for operators and technical managers: live
              vessel intelligence, CDI and RISQ inspections, and corrective
              actions tracked to closure.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 max-w-md">
              <Cue Icon={Radar}           label="Fleet readiness score" />
              <Cue Icon={Ship}            label="Live vessel intelligence" />
              <Cue Icon={ClipboardCheck}  label="CDI · RISQ · TMSA" />
              <Cue Icon={FileText}        label="Findings & readiness packs" />
            </div>

            {/* Product preview — a live-feeling readiness card */}
            <ReadinessPreviewCard />
          </div>

          {/* trust strip */}
          <div className="relative pt-8 border-t border-slate-800/80 max-w-xl">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-[11px] text-slate-500 uppercase tracking-wider">
              <TrustItem>79,000+ vessels tracked</TrustItem>
              <TrustItem>AIS every 5 minutes</TrustItem>
              <TrustItem>PSC &amp; sanctions synced daily</TrustItem>
              <TrustItem>Encrypted at rest &amp; in transit</TrustItem>
            </div>
          </div>
        </section>

        {/* ── RIGHT: sign-in card ──────────────────────────────────── */}
        <section className="relative flex flex-col items-center justify-center px-5 sm:px-8 py-10 lg:py-14">
          <div className="w-full max-w-md">
            {/* Mobile-only brand */}
            <div className="lg:hidden mb-8 flex justify-center">
              <BrandLogo size="md" tagline tone="dark" />
            </div>

            {/* Soft glow behind the card */}
            <div className="absolute -inset-x-2 -inset-y-4 lg:-inset-y-8 bg-cyan-500/5 blur-3xl pointer-events-none" />

            <div className="relative">
              <div
                className="rounded-3xl border border-slate-700/75 overflow-hidden"
                style={{
                  background: 'rgba(15,23,42,0.86)',
                  backdropFilter: 'blur(18px)',
                  WebkitBackdropFilter: 'blur(18px)',
                  boxShadow:
                    '0 0 0 1px rgba(6,182,212,0.06) inset, 0 24px 60px -25px rgba(0,0,0,0.6)',
                }}
              >
                {/* hairline top border in cyan */}
                <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

                <div className="px-6 sm:px-8 py-7 sm:py-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                    Sign in to{' '}
                    <span className="text-slate-100">
                      Varun
                      <span className="text-cyan-400 font-extrabold">AI</span>
                      <span className="text-slate-100">q</span>
                    </span>
                  </h2>
                  <p className="mt-1.5 text-sm text-slate-400">
                    Continue to your maritime compliance workspace.
                  </p>

                  <div className="mt-7">
                    <SignIn
                      routing="path"
                      path="/login"
                      signUpUrl="/register/viewer"
                      appearance={{
                        variables: {
                          colorPrimary:         '#06b6d4',
                          colorBackground:      'transparent',
                          colorInputBackground: 'rgba(30,41,59,0.8)',
                          colorInputText:       '#F8FAFC',
                          colorText:            '#F8FAFC',
                          colorTextSecondary:   '#94A3B8',
                          colorNeutral:         '#334155',
                          borderRadius:         '0.75rem',
                          fontFamily:
                            'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                          fontSize:             '14px',
                        },
                        elements: {
                          rootBox:                  'w-full',
                          card:                     'bg-transparent shadow-none border-0 p-0 w-full',
                          // hide Clerk's own header — we render our own above
                          header:                   'hidden',
                          headerTitle:              'hidden',
                          headerSubtitle:           'hidden',
                          socialButtonsBlockButton:
                            'border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-100 transition-colors h-12 rounded-xl',
                          socialButtonsBlockButtonText: 'font-medium',
                          dividerLine:              'bg-slate-700/60',
                          dividerText:              'text-slate-500 text-xs uppercase tracking-wider',
                          formFieldLabel:           'text-slate-300 text-xs uppercase tracking-wider font-semibold',
                          formFieldInput:
                            'bg-slate-800/80 border border-slate-700 text-white placeholder-slate-500 ' +
                            'focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 ' +
                            'h-12 rounded-xl px-3.5',
                          formButtonPrimary:
                            'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 ' +
                            'text-white font-semibold h-12 rounded-xl shadow-md shadow-cyan-500/20 ' +
                            'transition-all duration-150 active:scale-[0.99]',
                          formFieldInputShowPasswordButton: 'text-slate-400 hover:text-white',
                          identityPreviewText:      'text-slate-300',
                          identityPreviewEditButton:'text-cyan-400 hover:text-cyan-300',
                          formFieldAction:          'text-cyan-400 hover:text-cyan-300',
                          // hide Clerk's own footer / branding to keep look fully VarunAIq
                          footer:                   'hidden',
                          footerActionText:         'hidden',
                          footerActionLink:         'hidden',
                          footerPagesLink:          'hidden',
                          badge:                    'hidden',
                          logoBox:                  'hidden',
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Registration cards */}
                <div className="px-6 sm:px-8 pb-6 sm:pb-7">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2.5">
                    New to VarunAIq?
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    <RegisterCard
                      to="/register/inspector"
                      title="Inspector access"
                      sub="Run CDI or RISQ inspections on board, offline capable."
                      tint="amber"
                    />
                    <RegisterCard
                      to="/register/viewer"
                      title="Operator access"
                      sub="Monitor fleet readiness, findings and readiness packs."
                      tint="cyan"
                    />
                  </div>
                </div>
              </div>

              {/* Demo CTA */}
              <Link
                to="/vetting/demo"
                className="group mt-4 flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl border border-slate-700/75 bg-slate-900/60 hover:border-cyan-500/40 hover:bg-slate-900/80 transition-all duration-150 active:scale-[0.99]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                    <Radar className="w-4.5 h-4.5 text-cyan-300" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-white">Explore demo workspace</div>
                    <div className="text-[11px] text-slate-400">Try a 5-minute walkthrough using sample vessel data.</div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-cyan-300 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer: T&C · Privacy · Copyright ────────────────────── */}
      <Footer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

/**
 * A miniature, non-interactive fleet-readiness card — gives the sign-in page
 * a live product feel without loading any data. Pure JSX/CSS, no assets.
 */
function ReadinessPreviewCard() {
  const rows: Array<{ name: string; score: number; band: string; cls: string }> = [
    { name: 'MT NORDIC HARRIER', score: 92, band: 'READY',    cls: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200' },
    { name: 'MT AEGEAN SPIRIT',  score: 71, band: 'WATCH',    cls: 'bg-cyan-500/10 border-cyan-500/40 text-cyan-200' },
    { name: 'MT PACIFIC DAWN',   score: 48, band: 'CONCERNS', cls: 'bg-amber-500/10 border-amber-500/40 text-amber-200' },
  ];
  return (
    <div
      className="mt-8 max-w-md rounded-2xl border border-slate-700/70 overflow-hidden"
      style={{
        background: 'rgba(15,23,42,0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      aria-hidden="true"
    >
      <div className="px-4 py-2.5 border-b border-slate-800 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
          Fleet readiness
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          live
        </span>
      </div>
      <div className="p-2 space-y-1.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-3 px-2.5 py-2 rounded-lg bg-slate-950/50">
            <span className="text-xs text-slate-200 font-medium truncate">{r.name}</span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold tabular-nums shrink-0 ${r.cls}`}>
              {r.score} · {r.band}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Cue({ Icon, label }: { Icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-cyan-300" />
      </div>
      <span className="text-sm text-slate-300 font-medium">{label}</span>
    </div>
  );
}

function TrustItem({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1 h-1 rounded-full bg-cyan-400" />
      {children}
    </span>
  );
}

function RegisterCard({
  to,
  title,
  sub,
  tint,
}: {
  to: string;
  title: string;
  sub: string;
  tint: 'amber' | 'cyan';
}) {
  const tintCls =
    tint === 'amber'
      ? 'border-amber-500/30 hover:border-amber-500/60 hover:bg-amber-500/5'
      : 'border-cyan-500/30 hover:border-cyan-500/60 hover:bg-cyan-500/5';
  const titleCls = tint === 'amber' ? 'text-amber-200' : 'text-cyan-200';
  return (
    <Link
      to={to}
      className={`block px-3.5 py-3 rounded-xl border bg-slate-800/50 transition-all duration-150 active:scale-[0.99] ${tintCls}`}
    >
      <div className={`text-sm font-semibold ${titleCls}`}>{title}</div>
      <div className="text-[11px] text-slate-400 leading-snug mt-0.5">{sub}</div>
    </Link>
  );
}

/** Subtle nautical-chart background — pure SVG so it stays sharp at any zoom
 *  and adds nothing to the bundle except a few KB of inlined paths. */
function NauticalBackdrop() {
  return (
    <>
      {/* depth/contour grid */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path
              d="M 48 0 L 0 0 0 48"
              fill="none"
              stroke="#06B6D4"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* depth contours */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.10]"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <g stroke="#06B6D4" fill="none" strokeWidth="1">
          <path d="M 0 220 Q 200 160 420 240 T 800 200" />
          <path d="M 0 280 Q 220 220 460 300 T 800 270" />
          <path d="M 0 340 Q 240 280 500 360 T 800 340" />
          <path d="M 0 410 Q 260 360 540 420 T 800 410" />
          <path d="M 0 480 Q 280 440 580 480 T 800 480" />
        </g>
      </svg>

      {/* radar arc */}
      <svg
        className="absolute -bottom-24 -right-24 w-[480px] h-[480px] pointer-events-none opacity-[0.18]"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="radar" cx="0%" cy="0%" r="100%">
            <stop offset="0%"  stopColor="#06B6D4" stopOpacity="0.45" />
            <stop offset="60%" stopColor="#06B6D4" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="0" cy="0" r="100" fill="url(#radar)" />
        <g stroke="#06B6D4" fill="none" strokeWidth="0.4" opacity="0.5">
          <circle cx="0" cy="0" r="30" />
          <circle cx="0" cy="0" r="55" />
          <circle cx="0" cy="0" r="80" />
        </g>
      </svg>

      {/* a few AIS-style dots */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.5]"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {[
          [120, 180], [260, 240], [380, 320], [500, 280], [600, 380],
          [180, 380], [320, 460], [620, 200], [480, 180], [700, 460],
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="2"   fill="#06B6D4" opacity="0.55" />
            <circle cx={x} cy={y} r="6"   fill="none" stroke="#06B6D4" opacity="0.18" />
          </g>
        ))}
      </svg>

      {/* soft cyan glow drifting bottom-left */}
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
    </>
  );
}

/** Footer with T&C, Privacy, Copyright — subtle, fixed at the bottom. */
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="px-6 lg:px-10 py-5 border-t border-slate-800/80 text-[11px] text-slate-500">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <span>© {year} VarunAIq. All rights reserved.</span>
          <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-slate-700" />
          <span className="hidden sm:inline-block">varunaiq.com</span>
        </div>
        <div className="flex items-center gap-5">
          <a
            href="/help/legal/terms"
            className="hover:text-cyan-300 transition-colors"
            title="Terms governing use of VarunAIq, including acceptable use, account responsibilities, and service availability."
          >
            Terms of Service
          </a>
          <a
            href="/help/legal/privacy"
            className="hover:text-cyan-300 transition-colors"
            title="How VarunAIq collects, uses, and protects inspector and vessel data, including data residency and retention."
          >
            Privacy Policy
          </a>
          <a
            href="/help/legal/security"
            className="hover:text-cyan-300 transition-colors"
            title="Encryption, role-based access control, audit logging, and our SOC 2 / ISO 27001 posture."
          >
            Security
          </a>
          <a
            href="/help/legal/dpa"
            className="hover:text-cyan-300 transition-colors"
            title="Data processing addendum for enterprise customers. GDPR / UK GDPR compliant."
          >
            DPA
          </a>
        </div>
      </div>
    </footer>
  );
}
